import React, { memo } from 'react';
import { Card, Badge, Button, Icon, Checkbox } from 'ui';
import { FormatCurrency } from 'utils/features/formatCurrency';

import type { IInvoices } from 'api/resources/overviews';
import { EStatus } from 'ui/types/core';

interface IInvoiceCardProps {
	invoice: IInvoices;
	isSelected: boolean;
	onOpenPdf: (url: string) => void;
	onPayNow: (id: string) => void;
	onSelect: (id: string) => void;
}

const InvoiceCard = memo(
	({
		invoice,
		isSelected,
		onOpenPdf,
		onPayNow,
		onSelect,
	}: IInvoiceCardProps) => {
		return (
			<Card
				border="2xl"
				className={`w-full p-4 bg-background text-text shadow-lg ${
					isSelected ? 'border-2 !border-[#3338A1]' : ''
				}`}
			>
				<div className="flex justify-between items-center mb-2">
					<div className="w-full flex items-center justify-between md:w-fit lg:w-fit md:items-center lg:items-center">
						<h3 className="text-lg font-normal text-primary-badge">
							{invoice.title}
						</h3>
						<Button
							variant="ghost"
							size="sm"
							className="text-primary"
							onClick={() => onOpenPdf(invoice.pdf)}
						>
							<Badge
								size="xs"
								className="rounded-md bg-purple-200 border-[1.5px] border-purple-200 text-purple-800"
							>
								<div className="text-purple-800 flex flex-row items-center gap-1">
									<span>PDF</span>
									<Icon name="arrow-up-right" className="fill-purple-800" />
								</div>
							</Badge>
						</Button>
					</div>
					{invoice.status !== EStatus.Paid && (
						<>
							<div className="hidden md-lg:block lg:block h-fit px-2 py-1 bg-gray-200 rounded-lg items-center">
								<Checkbox
									label="Select"
									checked={isSelected}
									onChange={() => onSelect(invoice.id)}
								/>
							</div>
							<div className="block h-fit px-2 py-1 bg-gray-200 rounded-lg items-center md-lg:hidden lg:hidden">
								<Checkbox
									hideLabel
									checked={isSelected}
									onChange={() => onSelect(invoice.id)}
									aria-label="Select invoice"
								/>
							</div>
						</>
					)}
				</div>
				<p className="text-xs text-gray-600 text-muted-foreground mb-4">
					Date: {invoice.date} | Items: {invoice.itemsQuantity}
				</p>
				<div className="p-3 rounded-lg border-[1.5px] border-grayColor shadow-sm md:flex lg:flex-row justify-between items-center bg-gray">
					<div
						className="
          flex w-full 
          justify-between 
          space-x-4 
          xs-sm:flex-col
          xs-sm:space-x-0
          xs-sm:gap-2
          xs-sm:items-center
          lg:justify-start
          "
					>
						<span className="flex items-baseline gap-2 text-2xl">
							<FormatCurrency value={invoice.total} className="font-dm-sans" />
							<span className="text-xs text-gray-500 font-normal text-muted-foreground">
								Total Amount
							</span>
						</span>
					</div>
					<div className="w-full flex text-sm mt-2 lg:m-0 flex-row items-center justify-center gap-3 lg:justify-end">
						{invoice.status === EStatus.Opened && (
							<Button
								variant="primary"
								size="sm"
								onClick={() => onPayNow(invoice.id)}
							>
								<div className="flex flex-row items-center gap-2">
									<Icon name="credit-card" className="fill-white" />
									Pay Now
								</div>
							</Button>
						)}

						{invoice.status !== EStatus.Pending && (
							<span className="text-sm flex flex-row items-center gap-2">
								{invoice.status === EStatus.Paid && (
									<div className="text-green-800 flex flex-row items-center gap-1">
										<Icon name="check-circle" className="fill-green-800" />
										<span>Paid</span>
									</div>
								)}
							</span>
						)}
					</div>
				</div>
			</Card>
		);
	},
);

InvoiceCard.displayName = 'InvoiceCard';

export { InvoiceCard };
